import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
} from "@tanstack/react-router";
import type { RouterHistory } from "@tanstack/react-router";
import { Box, Button, Typography } from "drift-ui";
import config from "./config";
import { hasForthLeafPermission } from "./forthLeafAccess";
import {
  createForthLeafPermissionsQuery,
  getForthLeafPermissionResponseStatus,
  resolveForthLeafIdentity,
  type ForthLeafIdentityClaims,
} from "./forthLeafPermissions";

interface ForthLeafRouterContext {
  getAccessTokenSilently: () => Promise<string>;
  getIdTokenClaims: () => Promise<ForthLeafIdentityClaims | undefined>;
  queryClient: QueryClient;
}

interface ForthLeafPermissionRequirement {
  permission: string;
}

interface RouteWithPermissionRequirement {
  options: {
    staticData?: {
      permission?: ForthLeafPermissionRequirement;
    };
  };
}

const routes = {
  projectShillelagh: "/project-shillelagh",
  skynetJr: "/skynet-jr",
} as const;

const navigationItems = [
  [routes.skynetJr, "Skynet Jr"],
  [routes.projectShillelagh, "Project Shillelagh"],
] as const;

const appShellPermission = {
  permission: config.forthLeafPermission,
};

export const queryClient = new QueryClient();

const HomeRoute = () => <Typography>Welcome to Forth Leaf.</Typography>;

const SkynetJrRoute = () => <Typography>{"Don't worry, it's just internal."}</Typography>;

const ProjectShillelaghRoute = () => (
  <Typography>{"If it doesn't work, hit it with a stick"}</Typography>
);

const NoAccessRoute = () => <Typography>You do not have access to this area.</Typography>;

const AppShellLayout = () => {
  const matchRoute = useMatchRoute();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        component="aside"
        sx={{
          borderRight: 1,
          borderColor: "divider",
          flex: "0 0 240px",
          padding: 3,
          position: "fixed",
          inset: "0 auto 0 0",
        }}
      >
        <Link to="/">
          <Typography component="h1" variant="h5">
            Forth Leaf
          </Typography>
        </Link>
        <Box component="nav" sx={{ display: "grid", gap: 1, marginTop: 4 }}>
          {navigationItems.map(([destination, label]) => {
            const isActive = Boolean(matchRoute({ to: destination, fuzzy: false }));

            return (
              <Button
                component={Link}
                key={destination}
                // @ts-expect-error Drift's Button type does not infer Link's `to` prop.
                to={destination}
                {...(isActive ? { "aria-current": "page" as const } : {})}
                variant={isActive ? "contained" : "text"}
              >
                {label}
              </Button>
            );
          })}
        </Box>
      </Box>
      <Box component="main" sx={{ marginLeft: "240px", padding: 4 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export const createForthLeafRouter = (history?: RouterHistory) => {
  const rootRoute = createRootRouteWithContext<ForthLeafRouterContext>()();

  const appShellLayout = createRoute({
    beforeLoad: async ({ context, location }) => {
      if (!config.enforceForthLeafAccess) {
        return;
      }

      const requirements = getForthLeafPermissionRequirements(router, location.pathname);
      if (requirements.length === 0) {
        return;
      }

      const identity = await resolveForthLeafIdentity(context.getIdTokenClaims);
      let activePermissions;

      try {
        activePermissions = await context.queryClient.ensureQueryData(
          createForthLeafPermissionsQuery({
            getAccessTokenSilently: context.getAccessTokenSilently,
            identity,
          }),
        );
      } catch (error) {
        if (getForthLeafPermissionResponseStatus(error) === 403) {
          throw redirect({
            to: "/no-access",
            search: { area: location.pathname },
          });
        }

        throw error;
      }
      const hasAccess = requirements.every((access) =>
        hasForthLeafPermission({
          activePermissions,
          requiredPermission: access.permission,
        }),
      );

      if (!hasAccess) {
        throw redirect({
          to: "/no-access",
          search: { area: location.pathname },
        });
      }
    },
    component: AppShellLayout,
    getParentRoute: () => rootRoute,
    id: "appShellLayout",
  });

  const homeRoute = createRoute({
    component: HomeRoute,
    getParentRoute: () => appShellLayout,
    path: "/",
  });
  const skynetJrRoute = createRoute({
    component: SkynetJrRoute,
    getParentRoute: () => appShellLayout,
    path: "skynet-jr",
    staticData: { permission: appShellPermission },
  });
  const projectShillelaghRoute = createRoute({
    component: ProjectShillelaghRoute,
    getParentRoute: () => appShellLayout,
    path: "project-shillelagh",
    staticData: { permission: appShellPermission },
  });
  const noAccessRoute = createRoute({
    component: NoAccessRoute,
    getParentRoute: () => rootRoute,
    path: "no-access",
    validateSearch: (search: Record<string, unknown>) => ({
      area: typeof search.area === "string" ? search.area : undefined,
    }),
  });
  const routeTree = rootRoute.addChildren([
    appShellLayout.addChildren([homeRoute, skynetJrRoute, projectShillelaghRoute]),
    noAccessRoute,
  ]);

  const options = {
    context: {
      getAccessTokenSilently: async () => {
        throw new Error("Forth Leaf access token acquisition is unavailable.");
      },
      getIdTokenClaims: async () => undefined,
      queryClient,
    },
    routeTree,
  };
  const router = createRouter(history ? { ...options, history } : options);

  return router;
};

export const getForthLeafPermissionRequirements = (
  targetRouter: ReturnType<typeof createForthLeafRouter>,
  pathname: string,
) =>
  targetRouter
    .matchRoutes(pathname)
    .map(
      (match) =>
        targetRouter.routesById[match.routeId] as RouteWithPermissionRequirement | undefined,
    )
    .flatMap((route) =>
      route?.options.staticData?.permission ? [route.options.staticData.permission] : [],
    );

export const router = createForthLeafRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
